import { Client, Report } from "@bugsnag/browser/dist/types/bugsnag-core";
import bugsnag from "@bugsnag/js";
import { INotifyOpts } from "@bugsnag/node/dist/types/bugsnag-core/common";
import bugsnagReact from "@bugsnag/plugin-react";
import React from "react";
import { actionLog, Store } from "src/store";
import { openSubModal } from "src/store/actions/onlineShopper/subModal";
import { errorStateSanitizer } from "src/store/stateSanitizer";

const API_KEY = "f427cbe1339f65528f783cf5d1687e0e";
const notifyReleaseStages = ["prod", "demo", "test"];
const ERRORS_TO_IGNORE = [
    "NetworkError when attempting to fetch resource.",
    "cancelled",
    "Aborted",
    "Failed to fetch",
    "Network request failed",
];

export let bugsnagClient: Client;

const determineIsOurError = (report: Report) => {
    const stackTrace = report.stacktrace || [];

    if (stackTrace.length === 0) {
        return false;
    }

    return stackTrace.reduce((isOurs: boolean, s: any) => {
        return isOurs || (s.file && ["localhost", "onlineshopper.dealerinspire"].some((f) => s.file.includes(f)));
    }, false);
};

const loadStateBeforeErrorSubmit = (store: Store) => (report: Report) => {
    if (!determineIsOurError(report)) {
        report.ignore();
        return;
    }

    if (report.errorClass === "ChunkLoadError") {
        report.ignore();
        return;
    }

    const currentStore = store.getState();

    if ((report as Report & { _handledState: { unhandled: boolean } })._handledState.unhandled) {
        void store.dispatch(openSubModal({ modalType: "unexpectedErrorModal" }));
    }

    if (!notifyReleaseStages.includes(currentStore.onlineShopper.env)) {
        report.ignore();
        return;
    }

    if (ERRORS_TO_IGNORE.includes(report.errorMessage)) {
        report.ignore();
        return;
    }

    report.updateMetaData("store", errorStateSanitizer(currentStore));
    report.updateMetaData("actions", actionLog.getLog()?.actions);
    report.updateMetaData("app", {
        referrer: document.referrer,
        srpLink: currentStore.onlineShopper.srpLink,
    });

    // Bugsnag won't group properly if this message includes variable data.
    if (report.errorMessage.includes("duplicate vin")) {
        report.groupingHash = "Collection can't contain duplicate vin";
    } else {
        report.groupingHash = report.errorMessage;
    }
};

export const log = (
    severity: "info" | "warning" | "error",
    error: Error | string,
    errorInfo?: object,
    opts?: INotifyOpts
) => {
    if (bugsnagClient) {
        const err = typeof error === "string" ? new Error(error) : error;

        return bugsnagClient.notify(err, {
            metaData: { "error info": errorInfo },
            severity,
            ...(opts || {}),
        });
    }

    return false;
};

export const logInfo = (message: string, messageInfo?: object, opts?: INotifyOpts) => {
    if (process.env.NODE_ENV !== "test") {
        console.log(message);
    }

    return log("info", message, messageInfo, opts);
};

export const logWarn = (error: Error | string, errorInfo?: object, opts?: INotifyOpts) => {
    if (process.env.NODE_ENV !== "test") {
        console.warn(error);
    }

    return log("warning", error, errorInfo, opts);
};

export const logError = (error: Error | string, errorInfo?: object, opts?: INotifyOpts) => {
    if (process.env.NODE_ENV !== "test") {
        console.error(error);
    }

    return log("error", error, errorInfo, opts);
};

export const initBugsnag = (appVersion: string, store: Store): Client => {
    const currentStore = store.getState();

    bugsnagClient = bugsnag({
        apiKey: API_KEY,
        appType: "web",
        appVersion,
        beforeSend: loadStateBeforeErrorSubmit(store),
        // notifyReleaseStages: notifyReleaseStages,
        releaseStage: currentStore.onlineShopper.env,
    });

    bugsnagClient.use(bugsnagReact, React);

    return bugsnagClient;
};
